import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Trash2, UsersRound } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SearchableSelect } from '@/components/common/searchable-select'
import { getErrorMessage } from '@/lib/api'
import { ensureUniqueCrewTemplateCode } from '@/lib/crew-template-code'
import { cn } from '@/lib/utils'
import { catalogRolesApi } from '@/services/catalog-roles'
import crewsApi from '@/services/crews'
import { jobsApi } from '@/services/jobs'
import type { CrewTemplate, CrewTemplateSlot, Job } from '@/types'

type TeamSlotDraft = {
  skill_role: string
  quantity: string
}

type JobTeamAssignmentControlsProps = {
  job: Job
  serviceInstanceId?: string | null
  onApplied?: () => void
  disabled?: boolean
  buttonClassName?: string
}

export function JobTeamAssignmentControls({
  job,
  serviceInstanceId,
  onApplied,
  disabled = false,
  buttonClassName,
}: JobTeamAssignmentControlsProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [teamSlots, setTeamSlots] = useState<TeamSlotDraft[]>([{ skill_role: '', quantity: '1' }])
  const [selectedTemplateId, setSelectedTemplateId] = useState('none')
  const [selectedStaffedTeamId, setSelectedStaffedTeamId] = useState('none')

  useEffect(() => {
    if (!open) {
      setCreating(false)
      return
    }

    setSelectedTemplateId(String(job.team_template ?? job.team_template_info?.id ?? 'none'))
    setSelectedStaffedTeamId('none')
  }, [open, job.id, job.team_template, job.team_template_info?.id])

  const teamTemplatesQuery = useQuery({
    queryKey: ['job-team-templates', serviceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listTemplates({
      service_instance_id: serviceInstanceId ?? undefined,
      active: true,
      page_size: 200,
    }),
    enabled: open,
    staleTime: 60_000,
  })

  const staffedTeamsQuery = useQuery({
    queryKey: ['job-staffed-teams', serviceInstanceId ?? 'none', selectedTemplateId],
    queryFn: () => crewsApi.listReadyCrews({
      crew_template_id: selectedTemplateId,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: open && !creating && Boolean(selectedTemplateId && selectedTemplateId !== 'none'),
    staleTime: 30_000,
  })

  const rolesQuery = useQuery({
    queryKey: ['catalog-roles', serviceInstanceId ?? 'none'],
    queryFn: () => catalogRolesApi.list({
      serviceInstanceId: serviceInstanceId ?? undefined,
      includeHidden: true,
    }),
    enabled: open && creating && Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const teamTemplateOptions = useMemo(
    () => [
      { value: 'none', label: 'Select team template' },
      ...(teamTemplatesQuery.data ?? []).map((template) => ({
        value: String(template.id),
        label: template.name || `Template #${template.id}`,
      })),
    ],
    [teamTemplatesQuery.data],
  )

  const staffedTeamOptions = useMemo(
    () => [
      { value: 'none', label: 'None (slots only)' },
      ...(staffedTeamsQuery.data ?? []).map((crew) => ({
        value: String(crew.id),
        label: crew.name || `Team #${crew.id}`,
      })),
    ],
    [staffedTeamsQuery.data],
  )

  const applyMutation = useMutation({
    mutationFn: async ({ templateId, staffedTeamId }: { templateId: string; staffedTeamId: string | null }) => {
      const kind = String(job.kind_code || '').trim().toLowerCase()
      const updateFn =
        kind === 'project'
          ? jobsApi.updateProject
          : kind === 'event'
            ? jobsApi.updateEvent
            : kind === 'committee'
              ? jobsApi.updateCommittee
              : kind === 'task'
                ? jobsApi.updateTask
                : jobsApi.update

      await updateFn(String(job.id), {
        title: job.title,
        description: job.description ?? '',
        category: job.category,
        status: job.status,
        team_template: templateId,
      })

      if (staffedTeamId) {
        return crewsApi.assignToJob({ job_id: job.id, crew_id: staffedTeamId })
      }

      return null
    },
    onSuccess: async (assignResult) => {
      await queryClient.invalidateQueries({ queryKey: ['job', String(job.id)] })
      await queryClient.invalidateQueries({ queryKey: ['job-assignments', String(job.id)] })
      await queryClient.invalidateQueries({ queryKey: ['assignments'] })

      if (assignResult?.assigned_count) {
        toast.success(`Team added. ${assignResult.assigned_count} worker${assignResult.assigned_count === 1 ? '' : 's'} assigned to open slots.`)
      } else {
        toast.success('Team template applied. Missing role slots were added.')
      }

      setOpen(false)
      onApplied?.()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleApply = () => {
    if (!selectedTemplateId || selectedTemplateId === 'none') {
      toast.error('Select a team template.')
      return
    }

    applyMutation.mutate({
      templateId: selectedTemplateId,
      staffedTeamId: selectedStaffedTeamId && selectedStaffedTeamId !== 'none' ? selectedStaffedTeamId : null,
    })
  }

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value)
    setSelectedStaffedTeamId('none')
  }

  const openCreateTeam = () => {
    setTeamName('')
    setTeamSlots([{ skill_role: '', quantity: '1' }])
    setCreating(true)
  }

  const createTeamMutation = useMutation({
    mutationFn: async () => {
      if (!serviceInstanceId) {
        throw new Error('Service instance is required')
      }

      const name = teamName.trim()
      if (!name) {
        throw new Error('Enter a team name.')
      }

      const validSlots = teamSlots.filter((slot) => slot.skill_role)
      if (validSlots.length === 0) {
        throw new Error('Add at least one team role.')
      }

      const categoryName = String(job.category_name ?? '').trim()
      const codeSource = categoryName ? `${categoryName}_${name}` : name
      const existingCodes = (teamTemplatesQuery.data ?? []).map((template) => template.code).filter(Boolean)
      const payload = {
        service_instance: serviceInstanceId,
        name,
        code: ensureUniqueCrewTemplateCode(codeSource, existingCodes),
        job_category: job.category ? String(job.category) : null,
        active: true,
        slots: validSlots.map((slot, index) => ({
          skill_role: slot.skill_role,
          quantity: Number(slot.quantity) || 1,
          is_primary: index === 0,
          slot_label: '',
          ordering: index,
        })) as CrewTemplateSlot[],
      }

      return crewsApi.createTemplate(payload)
    },
    onSuccess: async (created) => {
      const createdTeam = created as CrewTemplate
      queryClient.setQueryData<CrewTemplate[]>(
        ['job-team-templates', serviceInstanceId ?? 'none'],
        (current) => {
          const list = current ?? []
          if (list.some((template) => String(template.id) === String(createdTeam.id))) {
            return list
          }
          return [...list, createdTeam]
        },
      )
      await queryClient.invalidateQueries({ queryKey: ['job-team-templates'] })
      await queryClient.invalidateQueries({ queryKey: ['crew-templates'] })
      setSelectedTemplateId(String(createdTeam.id))
      setSelectedStaffedTeamId('none')
      setCreating(false)
      toast.success('Team created')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const roleOptions = rolesQuery.data ?? []
  const createBusy = createTeamMutation.isPending || applyMutation.isPending

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn('rounded-lg', buttonClassName ?? 'w-full')}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <UsersRound className="mr-2 h-4 w-4" />
        Add team
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{creating ? 'Create team' : 'Add team'}</DialogTitle>
            <DialogDescription>
              {creating
                ? 'Create a team for this job, then apply it to add the role slots.'
                : 'Apply a team to add missing role slots. Optionally assign a staffed team to fill open slots without replacing workers already on the job.'}
            </DialogDescription>
          </DialogHeader>

          {creating ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="create-team-name">Name</Label>
                <Input
                  id="create-team-name"
                  value={teamName}
                  onChange={(event) => setTeamName(event.target.value)}
                  placeholder="Deep cleaning team"
                  disabled={createBusy}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Roles</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={createBusy}
                    onClick={() => setTeamSlots((current) => [...current, { skill_role: '', quantity: '1' }])}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add role
                  </Button>
                </div>
                {teamSlots.map((slot, index) => (
                  <div key={index} className="grid grid-cols-[minmax(0,1fr)_72px_auto] items-end gap-2">
                    <div className="space-y-2">
                      {index === 0 ? <Label>Skill role</Label> : <span className="sr-only">Skill role</span>}
                      <Select
                        value={slot.skill_role || undefined}
                        onValueChange={(value) => setTeamSlots((current) => current.map((row, rowIndex) => (
                          rowIndex === index ? { ...row, skill_role: value } : row
                        )))}
                        disabled={rolesQuery.isLoading || createBusy}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={rolesQuery.isLoading ? 'Loading roles...' : 'Select role'} />
                        </SelectTrigger>
                        <SelectContent>
                          {roleOptions.length === 0 ? (
                            <SelectItem value="__empty" disabled>
                              {rolesQuery.isLoading ? 'Loading roles...' : 'No skill roles found'}
                            </SelectItem>
                          ) : (
                            roleOptions.map((role) => (
                              <SelectItem key={String(role.id)} value={String(role.id)}>{role.name}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      {index === 0 ? <Label>Qty</Label> : <span className="sr-only">Quantity</span>}
                      <Input
                        value={slot.quantity}
                        inputMode="numeric"
                        disabled={createBusy}
                        onChange={(event) => setTeamSlots((current) => current.map((row, rowIndex) => (
                          rowIndex === index ? { ...row, quantity: event.target.value } : row
                        )))}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={createBusy || teamSlots.length === 1}
                      onClick={() => setTeamSlots((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="add-team-template">Team</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    disabled={createBusy || !serviceInstanceId}
                    onClick={openCreateTeam}
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Create team
                  </Button>
                </div>
                <SearchableSelect
                  id="add-team-template"
                  ariaLabel="Team"
                  value={selectedTemplateId}
                  onValueChange={handleTemplateChange}
                  options={teamTemplateOptions}
                  placeholder={teamTemplatesQuery.isLoading ? 'Loading teams...' : 'Select team'}
                  searchPlaceholder="Search teams"
                  emptyText="No teams found."
                  disabled={teamTemplatesQuery.isLoading || createBusy}
                />
              </div>

              {selectedTemplateId !== 'none' ? (
                <div className="space-y-2">
                  <Label htmlFor="add-team-staffed">Staffed team</Label>
                  <SearchableSelect
                    id="add-team-staffed"
                    ariaLabel="Staffed team"
                    value={selectedStaffedTeamId}
                    onValueChange={setSelectedStaffedTeamId}
                    options={staffedTeamOptions}
                    placeholder={staffedTeamsQuery.isLoading ? 'Loading teams...' : 'Select staffed team (optional)'}
                    searchPlaceholder="Search staffed teams"
                    emptyText="No ready teams for this template."
                    disabled={staffedTeamsQuery.isLoading || createBusy}
                  />
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter>
            {creating ? (
              <>
                <Button type="button" variant="outline" onClick={() => setCreating(false)} disabled={createBusy}>
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => createTeamMutation.mutate()}
                  disabled={createBusy || !teamName.trim()}
                >
                  {createTeamMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create team
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={createBusy}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleApply}
                  disabled={selectedTemplateId === 'none' || createBusy}
                >
                  {applyMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Apply
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default JobTeamAssignmentControls
